/**
 * Model runs — the picks behind the score matrix's run dropdown.
 *
 * A matrix column is a VARIANT (what was measured); a model run is WHICH
 * upload the cells read. Training adds model runs to the same task × variant
 * grid, never new columns, so the matrix needs a way to step back through
 * them rather than being pinned to whatever landed last.
 *
 * A run is identified by (model_slug, run date) across the whole group: one
 * push fans out into one run per (task, variant) all sharing that eval's
 * timestamp, so the batch collapses to ONE pick that fills every task it
 * covered. Tasks the batch skipped render dashed.
 *
 * Derived entirely from `VariantScore.history` (and `reference_history` for
 * baselines), which already carry one `{date, score, model}` point per run of
 * a cell, oldest→newest — the API's own `sort_runs` order. That ordering is
 * the whole reason `pointForRun` can take the LAST match and land on the same
 * run the API calls latest.
 *
 * Reference runs (random / a baseline / SOTA) are pickable so you can see how
 * a baseline scored task by task, but they are NEVER the default and stay in
 * their own dropdown group — they're a level to beat, not a checkpoint.
 */

import { formatRelative } from '@/components/evals/format'
import type { EvalTaskSummary, ScorePoint, VariantScore } from '@/api/generated/model'

export interface ModelRun {
  /** Radix option value — namespaced like the chart's `task:` picks. */
  key: string
  modelSlug: string
  /** The run date as the API sent it, for `pointForRun` to match against. */
  date: string
  /** Dropdown caption, e.g. `qwen3.5-4b · Jul 29, 2026`. */
  label: string
  /** A baseline rather than a datapoint — grouped apart, never the default. */
  isReference: boolean
}

function runKey(modelSlug: string, date: string, isReference: boolean): string {
  return `${isReference ? 'ref' : 'run'}:${modelSlug}|${date}`
}

interface RawRun {
  modelSlug: string
  date: string
  time: number
  isReference: boolean
}

/**
 * Every (model, date) upload seen anywhere in the group: scored runs NEWEST
 * FIRST, then reference runs newest first. So `modelRuns(tasks)[0]` is the
 * default pick and is a real datapoint whenever the group has one — pushing a
 * fresh baseline must never change what the matrix shows by default. Ties on
 * the same instant sort by model slug, keeping order stable across refetches.
 *
 * Identity uses the parsed epoch, not the raw string, so two encodings of the
 * same instant can't split one batch into two picks. A (model, instant) seen
 * as both scored and reference keeps its SCORED identity — `history` is the
 * authority on what counts.
 */
export function modelRuns(tasks: EvalTaskSummary[]): ModelRun[] {
  const runs = new Map<string, RawRun>()
  const add = (point: ScorePoint, isReference: boolean): void => {
    const time = new Date(point.date).getTime()
    const identity = `${point.model}|${time.toString()}`
    const seen = runs.get(identity)
    // Scored wins a collision, and only ever upgrades a reference entry.
    if (seen === undefined || (seen.isReference && !isReference)) {
      runs.set(identity, { modelSlug: point.model, date: point.date, time, isReference })
    }
  }
  for (const task of tasks) {
    for (const cell of task.variant_scores) {
      for (const point of cell.history) add(point, false)
      for (const point of cell.reference_history ?? []) add(point, true)
    }
  }

  const byRecency = (left: RawRun, right: RawRun): number => {
    if (left.time !== right.time) return right.time - left.time
    return left.modelSlug.localeCompare(right.modelSlug)
  }
  const all = [...runs.values()]
  const ordered = [
    ...all.filter((run) => !run.isReference).sort(byRecency),
    ...all.filter((run) => run.isReference).sort(byRecency),
  ]

  return withDistinctLabels(ordered)
}

/**
 * Label each run, adding the time of day only where the date alone would be
 * ambiguous — several checkpoints of one model can land on the same day.
 * Keys stay unique regardless; this is purely so the dropdown reads right.
 */
function withDistinctLabels(ordered: RawRun[]): ModelRun[] {
  const dayLabels = ordered.map((run) => `${run.modelSlug} · ${formatRelative(run.date)}`)
  const collisions = new Set(
    dayLabels.filter((label, index) => dayLabels.indexOf(label) !== index)
  )
  return ordered.map((run, index) => {
    const dayLabel = dayLabels[index]
    if (dayLabel === undefined) {
      throw new Error(`unreachable: no label for model run ${run.modelSlug}`)
    }
    const time = new Date(run.date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
    return {
      key: runKey(run.modelSlug, run.date, run.isReference),
      modelSlug: run.modelSlug,
      date: run.date,
      label: collisions.has(dayLabel) ? `${dayLabel}, ${time}` : dayLabel,
      isReference: run.isReference,
    }
  })
}

/**
 * This cell's score under one model run, or undefined when the run never
 * touched this (task, variant) pair.
 *
 * Reads whichever series the run belongs to, so a reference pick shows the
 * baseline's own per-task scores. Takes the LAST match: both series arrive in
 * the API's `sort_runs` order, so when a pair somehow holds two runs of the
 * same model at the same instant, the last one is the one every API rollup
 * already treats as latest.
 */
export function pointForRun(cell: VariantScore, run: ModelRun): ScorePoint | undefined {
  const time = new Date(run.date).getTime()
  const series = run.isReference ? cell.reference_history ?? [] : cell.history
  let match: ScorePoint | undefined
  for (const point of series) {
    if (point.model === run.modelSlug && new Date(point.date).getTime() === time) {
      match = point
    }
  }
  return match
}
